import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { FocusEvent, useState } from "react";

import { ModelState } from "../../interfaces/modelState";
import { Tooltip } from "../tooltip/Tooltip";
import { hasValidationMessage } from "../validationMessage/hasValidationMessage";
import { ValidationMessage } from "../validationMessage/ValidationMessage";
import formatDateInput from "./sanitizer/formatDateInput";
import unobtrusiveValidation from "./unobtrusiveValidation";
import useFieldData from "./useFieldData";
import useFieldName from "./useFieldName";
import { Validators } from "./validator/validators";

interface Props {
  fieldData: Record<string, Field<string>>;
  name: string;
  fieldName: string;
  modelState?: ModelState | null;
  setModelState?: (modelState: ModelState) => void;
  inputValue?: string;
  id?: string;
  validators?: Validators<string>;
  onChange?: (value: string) => void;
  propertyName: string;
  required?: boolean;
  disabled?: boolean;
  onEditClick?: () => void;
}

const DateInput = (props: Props) => {
  const { name, fieldName } = useFieldName(props.name, props.fieldName);
  const { heading, placeholder, info, tooltip } = useFieldData(props.fieldData, name);
  const [isDirty, setDirty] = useState(Boolean(props.inputValue));
  const modelState = props.modelState;

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    const fieldValue = e.target.value;
    const dateOut = formatDateInput(fieldValue) || "";
    let dirty = isDirty;

    if (!dirty && dateOut) {
      setDirty(true);
      dirty = true;
    }

    if (dirty && props.setModelState) {
      props.setModelState(unobtrusiveValidation(props.validators, modelState, props.propertyName, dateOut));
    }

    e.target.value = dateOut;
    if (props.onChange) {
      props.onChange(dateOut);
    }
  };

  const id = props.id || fieldName;
  const errorId = `${id}-error`;
  const hasError = hasValidationMessage(modelState, props.propertyName);

  return (
    <div className="form-row">
      <div className="form-item">
        <div className="form-input">
          {heading.value && (
            <div className="form-label">
              <label htmlFor={id}>{heading.value}</label>
              <Tooltip fieldName={fieldName} value={tooltip.value} />
            </div>
          )}
          <input
            required={props.required}
            id={id}
            name={fieldName}
            placeholder={placeholder.value}
            type="text"
            autoComplete="off"
            defaultValue={props.inputValue}
            onBlur={onBlur}
            disabled={props.disabled}
            aria-invalid={hasError}
            aria-errormessage={hasError ? errorId : undefined}
          />
        </div>
        {info && <span>{info.value}</span>}
        {props.onEditClick && (
          <button id={`${fieldName}_modify_id`} className="text-link" onClick={props.onEditClick}>
            Wijzigen
          </button>
        )}
      </div>
      <ValidationMessage modelState={modelState} property={props.propertyName} id={errorId} />
    </div>
  );
};

export default DateInput;
